import React, { useEffect, useState, useRef } from "react";
import { Badge } from "../components/ui/badge";
import { Link, useNavigate } from "react-router-dom";

import {
  Users,
  Calendar,
  Clock,
  CheckSquare,
  TrendingUp,
  FileText,
  Bell,
  UserPlus,
  LogIn,
  LogOut,
  DollarSign,
  ChevronRight,
  ArrowRight,
  Download,
  ClipboardList,
  Activity,
  Wallet,
  FolderOpen,
  PlusCircle,
} from "lucide-react";

import { getDashboardData, getRecentActivities } from "../api/dashboard";
import { tasksApi } from "../api/tasks";
import { getLeaves } from "../api/leaves";
import { getAttendanceSummary } from "../api/attendance";
import { projectsApi } from "../api/projects";
import { salaryApi } from "../api/salary";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";

// Inline count-up component
const Counter: React.FC<{ value: number; suffix?: string; prefix?: string; duration?: number }> = ({ value, suffix = "", prefix = "", duration = 1400 }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { setDisplay(value); return; }
    const start = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(value * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span ref={ref}>{prefix}{Math.round(display).toLocaleString("en-IN")}{suffix}</span>;
};

/* ──────────────── Inline SVG charts ──────────────── */
type Segment = { label: string; value: number; color: string };

// Animated donut/pie with segments, labels, and a center stat
const DonutChart: React.FC<{ segments: Segment[]; size?: number; thickness?: number; centerLabel?: string; centerValue?: React.ReactNode }> = ({
  segments,
  size = 160,
  thickness = 18,
  centerLabel,
  centerValue,
}) => {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let acc = 0;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--gt-surface-2)" strokeWidth={thickness} />
        {segments.map((seg, i) => {
          const len = (seg.value / total) * c;
          const offset = c - acc;
          acc += len;
          return (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={thickness}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={offset}
              strokeLinecap="butt"
              style={{ transition: "stroke-dasharray 1100ms cubic-bezier(0.22,1,0.36,1), stroke-dashoffset 1100ms cubic-bezier(0.22,1,0.36,1)" }}
            />
          );
        })}
      </svg>
      {(centerValue || centerLabel) && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {centerValue && <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 32, fontWeight: 500, letterSpacing: "-0.025em", lineHeight: 1, color: "var(--gt-text-primary)" }}>{centerValue}</div>}
          {centerLabel && <div className="dash-eyebrow" style={{ marginTop: 6 }}>{centerLabel}</div>}
        </div>
      )}
    </div>
  );
};

// Vertical bars trend chart
const BarChart: React.FC<{ data: { label: string; value: number; sub?: string }[]; max?: number; color?: string; height?: number; valueFormat?: (v: number) => string }> = ({
  data,
  max,
  color = "var(--gt-primary)",
  height = 140,
  valueFormat,
}) => {
  const m = max ?? Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="w-full">
      <div className="flex items-end gap-2 w-full" style={{ height }}>
        {data.map((d, i) => {
          const h = Math.max((d.value / m) * 100, d.value > 0 ? 4 : 0);
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
              <div className="dash-mono" style={{ fontSize: 10, color: "var(--gt-text-secondary)", fontWeight: 600, opacity: d.value > 0 ? 1 : 0.4 }}>
                {valueFormat ? valueFormat(d.value) : d.value}
              </div>
              <div className="w-full rounded-t-md relative overflow-hidden" style={{ height: `calc(100% - 18px)`, background: "var(--gt-surface-2)", display: "flex", alignItems: "flex-end" }}>
                <div
                  className="w-full rounded-t-md group-hover:brightness-110"
                  style={{
                    height: `${h}%`,
                    background: color,
                    transition: "height 1100ms cubic-bezier(0.22,1,0.36,1)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18)",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 w-full mt-2">
        {data.map((d, i) => (
          <div key={i} className="flex-1 text-center dash-mono" style={{ fontSize: 9.5, color: "var(--gt-text-tertiary)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {d.label}
          </div>
        ))}
      </div>
    </div>
  );
};

// Horizontal stacked bar — sums to 100%
const StackedBar: React.FC<{ segments: Segment[]; height?: number }> = ({ segments, height = 12 }) => {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  return (
    <div className="w-full">
      <div className="flex w-full overflow-hidden rounded-full" style={{ height, background: "var(--gt-surface-2)" }}>
        {segments.map((seg, i) => (
          <div
            key={i}
            title={`${seg.label} · ${seg.value}`}
            style={{
              width: `${(seg.value / total) * 100}%`,
              background: seg.color,
              transition: "width 1100ms cubic-bezier(0.22,1,0.36,1)",
            }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
        {segments.map((seg, i) => {
          const pct = Math.round((seg.value / total) * 100);
          return (
            <div key={i} className="flex items-center gap-1.5 dash-mono" style={{ fontSize: 11, color: "var(--gt-text-secondary)" }}>
              <span className="w-2 h-2 rounded-full" style={{ background: seg.color }} />
              <span style={{ fontWeight: 600 }}>{seg.label}</span>
              <span style={{ color: "var(--gt-text-tertiary)" }}>· {seg.value} ({pct}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Tiny inline sparkline for module tiles
const Sparkline: React.FC<{ data: number[]; width?: number; height?: number; color?: string }> = ({ data, width = 80, height = 24, color = "var(--gt-primary)" }) => {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1 || 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(" ");
  const areaPoints = `0,${height} ${points} ${width},${height}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <polygon points={areaPoints} fill={color} opacity={0.12} />
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={(data.length - 1) / (data.length - 1 || 1) * width} cy={height - ((data[data.length - 1] - min) / range) * height} r={2.5} fill={color} />
    </svg>
  );
};
/* ──────────────── /Inline SVG charts ──────────────── */

export function Dashboard() {
  const [stats, setStats] = useState<any>(null);
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [displayActivities, setDisplayActivities] = useState<any[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [taskStats, setTaskStats] = useState<any>(null);
  const [leavesData, setLeavesData] = useState<any[]>([]);
  const [attendanceTrend, setAttendanceTrend] = useState<{ label: string; value: number }[]>([]);
  const [projectsList, setProjectsList] = useState<any[]>([]);
  const [salaryStatus, setSalaryStatus] = useState<any>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const todayDate = new Date().toISOString().split("T")[0];

      // Fetch dashboard stats from existing API
      const dashboardResponse = await getDashboardData(todayDate);

      // Fetch open tasks count from tasks API
      let openTasksCount = 0;
      try {
        const tasksResponse = await tasksApi.list({ page_size: 100 });
        const allTasks = tasksResponse.data.items || [];
        openTasksCount = allTasks.filter(t => !['DONE', 'CANCELLED'].includes(t.status)).length;
      } catch (taskError) {
        console.error("Error fetching tasks:", taskError);
      }

      // Extract stats from dashboard response
      if (dashboardResponse) {
        setStats({
          total_employees: dashboardResponse.total_employees,
          activeEmployees: dashboardResponse.activeEmployees,
          pending_leaves: dashboardResponse.pending_leaves,
          todays_present: dashboardResponse.todays_present,
          openTasks: openTasksCount,
          completedTasks: dashboardResponse.completedTasks,
        });
      }

      // Fetch recent activities from new API
      const activitiesResponse = await getRecentActivities(todayDate, 50);

      // Handle different response structures for activities
      let activitiesData = [];
      if (activitiesResponse && activitiesResponse.activities) {
        activitiesData = activitiesResponse.activities;
      } else if (Array.isArray(activitiesResponse)) {
        activitiesData = activitiesResponse;
      } else if (activitiesResponse && activitiesResponse.data) {
        activitiesData = activitiesResponse.data;
      } else {
        activitiesData = [];
      }

      // Process and shuffle activities to get a mixed display
      const processedActivities = processAndShuffleActivities(activitiesData);
      setActivities(processedActivities);

      // Set initial display activities (first 3)
      setDisplayActivities(processedActivities.slice(0, 3));
      setCurrentPageIndex(0);

      // Fetch additional analytics data in parallel — all best-effort
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 6);
      const fmt = (d: Date) => d.toISOString().split("T")[0];

      const yyyy = String(today.getFullYear());
      const mm = String(today.getMonth() + 1).padStart(2, "0");

      const [tStatsRes, leavesRes, attRes, projectsRes, salaryRes] = await Promise.allSettled([
        tasksApi.getDashboardStats(),
        getLeaves(),
        getAttendanceSummary(fmt(sevenDaysAgo), todayDate),
        projectsApi.list({ page_size: 100 }),
        salaryApi.getSalaryStatus(yyyy, mm, []),
      ]);

      if (tStatsRes.status === "fulfilled") {
        const data = (tStatsRes.value as any)?.data || tStatsRes.value;
        setTaskStats(data || null);
      }
      if (leavesRes.status === "fulfilled") {
        setLeavesData(leavesRes.value?.leaves || []);
      }
      if (attRes.status === "fulfilled") {
        // Try to extract a 7-day attendance trend from the response — accept several shapes
        const raw = attRes.value;
        const items: any[] = Array.isArray(raw) ? raw : raw?.summary || raw?.data || raw?.days || [];
        const buckets: { label: string; value: number }[] = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date(today);
          d.setDate(today.getDate() - i);
          const key = fmt(d);
          const match = items.find((it: any) => (it.date || it.day || it._id || "").startsWith(key));
          buckets.push({
            label: d.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 3),
            value: Number(match?.present_count ?? match?.present ?? match?.count ?? 0),
          });
        }
        setAttendanceTrend(buckets);
      }
      if (projectsRes.status === "fulfilled") {
        const raw = (projectsRes.value as any)?.data;
        const items: any[] = Array.isArray(raw) ? raw : raw?.items || raw?.projects || raw?.data || [];
        setProjectsList(items);
      }
      if (salaryRes.status === "fulfilled") {
        const raw = (salaryRes.value as any)?.data || salaryRes.value;
        setSalaryStatus(raw?.status || raw || null);
      }

    } catch (error: any) {
      console.error("Error fetching dashboard data:", error);
      toast({
        // title: "Error",
        description: error.message || "Failed to load dashboard data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Process and SHUFFLE all activities to get a true mix of different types
  const processAndShuffleActivities = (rawActivities: any[]) => {
    const processedActivities: any[] = [];

    for (const activity of rawActivities) {
      const type = activity.type?.toLowerCase();
      const title = (activity.title || "").toLowerCase();
      const description = (activity.description || "").toLowerCase();
      // const employee_name = activity.employee_name || "Employee";
      const employee_name =
  activity.employee_name && activity.employee_name !== "Unknown"
    ? activity.employee_name
    : "Admin";
      const timestamp = activity.timestamp;

      // Handle Attendance (Clock In/Out)
      if (type === "attendance") {
        const status = activity.meta?.status || "";
        if (status === "Clock Out" || title.includes("clocked out")) {
          processedActivities.push({
            ...activity,
            displayType: "clock_out",
            displayMessage: `${employee_name} clocked out`,
            icon: "clock_out",
            color: "red",
            time: timestamp,
            badge: "Checked Out",
            badgeColor: "red"
          });
        } else if (status === "Clock In" || title.includes("clocked in")) {
          processedActivities.push({
            ...activity,
            displayType: "clock_in",
            displayMessage: `${employee_name} clocked in`,
            icon: "clock_in",
            color: "green",
            time: timestamp,
            badge: "Checked In",
            badgeColor: "green"
          });
        } else if (status === "Additional In" || description.includes("additional in")) {
          processedActivities.push({
            ...activity,
            displayType: "clock_in",
            displayMessage: `${employee_name} clocked in (Additional)`,
            icon: "clock_in",
            color: "green",
            time: timestamp,
            badge: "Additional In",
            badgeColor: "green"
          });
        }
      }
      // Handle Leave Activities
      else if (type === "leave") {
        const leaveStatus = activity.meta?.status || "";
        const leaveType = activity.meta?.leave_type || "";
        const days = activity.meta?.total_days || "";

        if (leaveStatus === "APPROVED") {
          processedActivities.push({
            ...activity,
            displayType: "leave_approved",
            displayMessage: `${employee_name}'s ${leaveType} leave approved (${days} days)`,
            icon: "leave",
            color: "green",
            time: timestamp,
            badge: "Leave Approved",
            badgeColor: "green"
          });
        } else if (leaveStatus === "PENDING") {
          processedActivities.push({
            ...activity,
            displayType: "leave_pending",
            displayMessage: `${employee_name} requested ${leaveType} leave`,
            icon: "leave",
            color: "orange",
            time: timestamp,
            badge: "Pending Approval",
            badgeColor: "orange"
          });
        }
      }
      // Handle Salary Activities
      else if (type === "salary") {
        const netSalary = activity.meta?.net_salary || 0;
        const month = activity.meta?.month || "";
        const year = activity.meta?.year || "";

        processedActivities.push({
          ...activity,
          displayType: "salary",
          displayMessage: `Salary processed for ${employee_name}: ₹${netSalary} for ${month} ${year}`,
          icon: "salary",
          color: "purple",
          time: timestamp,
          badge: "Salary Processed",
          badgeColor: "purple"
        });
      }

      else if (type === "task") {
        const taskStatus = activity.meta?.status || "";
        const taskTitle = activity.title || "Task"; 

        if (taskStatus === "DONE") {
          processedActivities.push({
            ...activity,
            displayType: "task_completed",
            displayMessage: `${employee_name} completed task: ${taskTitle}`,
            icon: "check_square",
            color: "green",
            time: activity.timestamp,
            badge: "Task Completed",
            badgeColor: "green"
          });
        }
        else if (taskStatus === "OPEN" || taskStatus === "TODO") {  
          processedActivities.push({
            ...activity,
            displayType: "task_open",
            displayMessage: `${employee_name} has a task: ${taskTitle}`,
            icon: "check_square",
            color: "orange",
            time: activity.timestamp,
            badge: "Open Task",
            badgeColor: "orange"
          });
        }
      }
    }

    // Sort by time (most recent first)
    processedActivities.sort((a, b) => {
      const timeA = a.time ? new Date(a.time).getTime() : 0;
      const timeB = b.time ? new Date(b.time).getTime() : 0;
      return timeB - timeA;
    });

    // Shuffle the activities to ensure a true mix (but keep recent ones first)
    const shuffled = shuffleArrayWithPriority(processedActivities);

    return shuffled;
  };

  // Shuffle array but keep recent items with higher priority
  const shuffleArrayWithPriority = (arr: any[]) => {
    if (arr.length <= 3) return arr;

    // Take first 8 most recent items and shuffle them
    const recentItems = arr.slice(0, Math.min(9, arr.length));
    const olderItems = arr.slice(Math.min(9, arr.length));

    // Fisher-Yates shuffle for recent items
    for (let i = recentItems.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [recentItems[i], recentItems[j]] = [recentItems[j], recentItems[i]];
    }

    return [...recentItems, ...olderItems];
  };

  // Auto-rotate activities every 30 seconds - showing different batches
  useEffect(() => {
    if (activities.length <= 3) return;

    const interval = setInterval(() => {
      setCurrentPageIndex((prev) => {
        const totalPages = Math.ceil(activities.length / 3);
        const nextPage = (prev + 1) % totalPages;
        const startIdx = nextPage * 3;
        const newDisplay = activities.slice(startIdx, startIdx + 3);

        // If we don't have enough items for a full page, show from beginning
        if (newDisplay.length < 3 && nextPage !== 0) {
          const fallbackDisplay = activities.slice(0, 3);
          setDisplayActivities(fallbackDisplay);
          return 0;
        }

        setDisplayActivities(newDisplay);
        return nextPage;
      });
    }, 30000);

    return () => clearInterval(interval);
  }, [activities]);

  useEffect(() => {
    fetchData();
  }, []);

  // Refresh data every 30 seconds
  // useEffect(() => {
  //   const refreshInterval = setInterval(() => {
  //     fetchData();
  //   }, 30000);

  //   return () => clearInterval(refreshInterval);
  // }, []);

  if (loading && activities.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const getStatsForRole = () => {
    const baseStats = [
      {
        title: "Total Employees",
        value: stats?.total_employees || 0,
        icon: Users,
        tone: "info" as const,
        description: `${stats?.activeEmployees || 0} active`,
      },
      {
        title: "Pending Leaves",
        value: stats?.pending_leaves || 0,
        icon: Calendar,
        tone: "warning" as const,
        description: "Requires approval",
      },
      {
        title: "Today's Attendance",
        value: `${stats?.todays_present || 0}`,
        icon: Clock,
        tone: "success" as const,
        description: "Present today",
      },
      {
        title: "Active Tasks",
        value: stats?.openTasks || 0,
        icon: CheckSquare,
        tone: "primary" as const,
        description: `${stats?.completedTasks || 0} completed`,
      },
    ];
    if (user?.role === "employee") {
      return baseStats.slice(1, 4);
    }
    return baseStats;
  };

  // Tone palette for premium stat tiles (matches Task Reports aesthetic)
  const STAT_TONES: Record<string, {
    iconBg: string;
    iconFg: string;
    valueColor?: string;
  }> = {
    info:    { iconBg: 'var(--gt-prio-low-bg)',  iconFg: 'var(--gt-prio-low-fg)' },
    warning: { iconBg: 'var(--gt-prio-med-bg)',  iconFg: 'var(--gt-prio-med-fg)' },
    success: { iconBg: 'var(--gt-status-done-bg)', iconFg: 'var(--gt-status-done-fg)', valueColor: 'var(--gt-accent-strong)' },
    danger:  { iconBg: 'var(--gt-prio-urgent-bg)', iconFg: 'var(--gt-prio-urgent-fg)', valueColor: '#7f1d1d' },
    primary: { iconBg: 'var(--gt-primary-container)', iconFg: 'var(--gt-primary-deep)' },
  };

  const getActivityIconComponent = (iconType: string) => {
    switch (iconType) {
      case "clock_in":
        return <LogIn className="h-4 w-4" />;
      case "clock_out":
        return <LogOut className="h-4 w-4" />;
      case "leave":
        return <Calendar className="h-4 w-4" />;
      case "employee":
        return <UserPlus className="h-4 w-4" />;
      case "salary":
        return <DollarSign className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getActivityColorClass = (color: string) => {
    switch (color) {
      case "green":
        return "bg-green-100 text-green-600";
      case "red":
        return "bg-red-100 text-red-600";
      case "orange":
        return "bg-orange-100 text-orange-600";
      case "blue":
        return "bg-blue-100 text-blue-600";
      case "purple":
        return "bg-purple-100 text-purple-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const getBadgeColorClass = (badgeColor: string) => {
    switch (badgeColor) {
      case "green":
        return "bg-green-50 text-green-600 border-green-200";
      case "red":
        return "bg-red-50 text-red-600 border-red-200";
      case "orange":
        return "bg-orange-50 text-orange-600 border-orange-200";
      case "purple":
        return "bg-purple-50 text-purple-600 border-purple-200";
      default:
        return "bg-gray-50 text-gray-600 border-gray-200";
    }
  };

  const getTimeAgo = (timestamp: string) => {
    if (!timestamp) return "Just now";
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "Recently";
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} min ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    } catch {
      return "Recently";
    }
  };

  const currentDisplay = displayActivities;

  return (
    <div className="gt-shell min-h-screen relative" style={{ background: "var(--gt-surface-1)" }}>
      <style>{`
        @keyframes dash-fade-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes dash-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes dash-shine { from { transform: translateX(-100%); } to { transform: translateX(220%); } }
        .dash-fade { animation: dash-fade-up 0.85s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
        .dash-pulse { animation: dash-pulse 1.6s ease-in-out infinite; }
        .dash-reveal { opacity: 0; transform: translateY(24px); transition: opacity 900ms cubic-bezier(0.22, 1, 0.36, 1), transform 900ms cubic-bezier(0.22, 1, 0.36, 1); }
        .dash-reveal.is-revealed { opacity: 1; transform: translateY(0); }
        .dash-stagger > * { opacity: 0; transform: translateY(18px); transition: opacity 700ms cubic-bezier(0.22, 1, 0.36, 1), transform 700ms cubic-bezier(0.22, 1, 0.36, 1); }
        .dash-stagger.is-revealed > * { opacity: 1; transform: translateY(0); }
        .dash-stagger.is-revealed > *:nth-child(1) { transition-delay: 0ms; }
        .dash-stagger.is-revealed > *:nth-child(2) { transition-delay: 70ms; }
        .dash-stagger.is-revealed > *:nth-child(3) { transition-delay: 140ms; }
        .dash-stagger.is-revealed > *:nth-child(4) { transition-delay: 210ms; }
        .dash-stagger.is-revealed > *:nth-child(5) { transition-delay: 280ms; }
        .dash-stagger.is-revealed > *:nth-child(6) { transition-delay: 350ms; }
        .dash-stagger.is-revealed > *:nth-child(7) { transition-delay: 420ms; }
        .dash-stagger.is-revealed > *:nth-child(8) { transition-delay: 490ms; }
        .dash-stagger.is-revealed > *:nth-child(9) { transition-delay: 560ms; }
        .dash-tile { transition: transform 360ms cubic-bezier(0.33, 1, 0.68, 1), box-shadow 360ms, border-color 240ms; cursor: pointer; }
        .dash-tile:hover { transform: translateY(-4px); border-color: var(--gt-primary) !important; box-shadow: 0 24px 48px -16px rgba(15,45,31,0.16), 0 4px 10px -4px rgba(15,45,31,0.06); }
        .dash-arrow { transition: transform 240ms cubic-bezier(0.33, 1, 0.68, 1); }
        .dash-tile:hover .dash-arrow { transform: translateX(4px); }
        .dash-shimmer { position: relative; overflow: hidden; }
        .dash-shimmer::after { content: ""; position: absolute; inset: 0; background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%); animation: dash-shine 2.6s cubic-bezier(0.4, 0, 0.2, 1) infinite; pointer-events: none; }
        .dash-eyebrow { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase; font-weight: 500; color: var(--gt-text-tertiary); }
        .dash-serif { font-family: 'Fraunces', 'Iowan Old Style', Georgia, serif !important; }
        .dash-mono { font-family: 'JetBrains Mono', ui-monospace, monospace !important; }
        .dash-dotted { background-image: radial-gradient(circle, currentColor 1px, transparent 1.2px); background-size: 8px 8px; background-repeat: repeat-x; }
        @media (prefers-reduced-motion: reduce) {
          .dash-fade, .dash-pulse, .dash-shimmer::after, .dash-reveal, .dash-stagger > * {
            animation: none !important;
            transition: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
        }
      `}</style>

      {/* DashRevealRunner — wraps a one-time IntersectionObserver call */}
      <DashRevealRunner />

      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-7 dash-fade">
        {/* WELCOME — editorial */}
        <div className="dash-reveal flex flex-col md:flex-row gap-5 items-start md:items-end justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="gt-leaf" />
              <span className="dash-eyebrow">i. Workspace</span>
            </div>
            <h1
              className="dash-serif"
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontSize: "clamp(34px, 4vw, 52px)",
                lineHeight: 1.0,
                letterSpacing: "-0.025em",
                fontWeight: 500,
                color: "var(--gt-text-primary)",
              }}
            >
              {getWelcomeMessage()},{" "}
              <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>
                {user?.name?.split(" ")[0] || "Admin"}
              </span>
            </h1>
            <p
              style={{
                marginTop: 10,
                fontSize: 15,
                color: "var(--gt-text-secondary)",
                maxWidth: 560,
                lineHeight: 1.55,
              }}
            >
              Here's what's happening with your{" "}
              <em className="dash-serif" style={{ fontStyle: "italic" }}>
                {user?.role === "admin" ? "organization" : "work"}
              </em>{" "}
              today.
            </p>
          </div>

          <div
            className="hidden md:flex items-center gap-4 rounded-2xl px-5 py-4"
            style={{
              background: "var(--gt-surface-0)",
              border: "1px solid var(--gt-border)",
              boxShadow: "var(--gt-elev-1)",
            }}
          >
            <div>
              <div className="dash-eyebrow" style={{ marginBottom: 4, fontSize: 9.5 }}>
                Today's date
              </div>
              <div
                className="dash-serif"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: 18,
                  letterSpacing: "-0.01em",
                  fontWeight: 500,
                  color: "var(--gt-text-primary)",
                }}
              >
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
            </div>
            <div className="dash-pulse w-2 h-2 rounded-full" style={{ background: "var(--gt-online)" }} />
          </div>
        </div>

        {/* KPI TILES — clickable, count-up */}
        <div className="dash-reveal dash-stagger grid grid-cols-2 lg:grid-cols-4 gap-4">
          {(() => {
            const all = [
              { title: "Total Employees", value: stats?.total_employees || 0, sub: `${stats?.activeEmployees || 0} active`, Icon: Users, href: "/employees", tone: "primary" },
              { title: "Pending Leaves", value: stats?.pending_leaves || 0, sub: "Requires approval", Icon: Calendar, href: "/leaves", tone: "warning" },
              { title: "Today's Attendance", value: stats?.todays_present || 0, sub: "Present today", Icon: Clock, href: "/attendance", tone: "success" },
              { title: "Active Tasks", value: stats?.openTasks || 0, sub: `${stats?.completedTasks || 0} completed`, Icon: CheckSquare, href: "/tasks/list", tone: "info" },
            ];
            const list = user?.role === "employee" ? all.slice(1, 4) : all;
            const TONE_BG: Record<string, string> = {
              primary: "var(--gt-primary-container)",
              warning: "var(--gt-prio-med-bg)",
              success: "var(--gt-status-done-bg)",
              info: "var(--gt-prio-low-bg)",
            };
            const TONE_FG: Record<string, string> = {
              primary: "var(--gt-primary-deep)",
              warning: "var(--gt-prio-med-fg)",
              success: "var(--gt-status-done-fg)",
              info: "var(--gt-prio-low-fg)",
            };
            return list.map((tile, i) => (
              <button
                key={i}
                onClick={() => navigate(tile.href)}
                className="dash-tile text-left rounded-2xl p-5 relative"
                style={{
                  background: "var(--gt-surface-0)",
                  border: "1px solid var(--gt-border)",
                  boxShadow: "var(--gt-elev-1)",
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="dash-eyebrow" style={{ fontSize: 10 }}>
                    {tile.title}
                  </span>
                  <span
                    className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{ background: TONE_BG[tile.tone], color: TONE_FG[tile.tone] }}
                  >
                    <tile.Icon className="w-4 h-4" />
                  </span>
                </div>
                <div
                  className="dash-serif"
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: 38,
                    fontWeight: 500,
                    letterSpacing: "-0.025em",
                    lineHeight: 1,
                    color: "var(--gt-text-primary)",
                  }}
                >
                  <Counter value={Number(tile.value) || 0} />
                </div>
                <div
                  className="dash-serif flex items-center gap-1.5"
                  style={{ marginTop: 10, fontSize: 12.5, color: "var(--gt-text-tertiary)", fontStyle: "italic" }}
                >
                  <span>{tile.sub}</span>
                  <ArrowRight className="dash-arrow w-3 h-3 ml-auto" style={{ color: "var(--gt-primary)" }} />
                </div>
              </button>
            ));
          })()}
        </div>

        {/* INSIGHTS — charts & graphs across modules */}
        <div>
          <div className="dash-reveal flex items-end justify-between mb-5 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="gt-leaf" />
                <span className="dash-eyebrow">ii. Insights</span>
              </div>
              <h2
                className="dash-serif"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: "clamp(24px, 2.6vw, 34px)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  fontWeight: 500,
                  color: "var(--gt-text-primary)",
                }}
              >
                A live <span style={{ fontStyle: "italic" }}>read</span> of every module
              </h2>
            </div>
            <span className="inline-flex items-center gap-1.5 dash-mono" style={{ fontSize: 10, color: "var(--gt-online)", letterSpacing: "0.2em", textTransform: "uppercase" }}>
              <span className="dash-pulse w-1.5 h-1.5 rounded-full" style={{ background: "var(--gt-online)" }} />
              Live data
            </span>
          </div>

          <div className="dash-reveal dash-stagger grid grid-cols-1 lg:grid-cols-6 gap-4">
            {/* 7-day attendance trend (wide) */}
            <button
              onClick={() => navigate("/attendance")}
              className="dash-tile text-left rounded-2xl p-6 lg:col-span-3"
              style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="dash-eyebrow mb-1.5">Attendance · 7 days</div>
                  <h3
                    className="dash-serif"
                    style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
                  >
                    Daily <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>presence</span>
                  </h3>
                </div>
                <div className="text-right">
                  <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 28, fontWeight: 500, letterSpacing: "-0.02em", color: "var(--gt-text-primary)", lineHeight: 1 }}>
                    <Counter value={attendanceTrend.reduce((s, d) => s + d.value, 0)} />
                  </div>
                  <div className="dash-eyebrow" style={{ fontSize: 9.5, marginTop: 4 }}>total this week</div>
                </div>
              </div>
              {attendanceTrend.length > 0 ? (
                <BarChart data={attendanceTrend} color="var(--gt-primary)" height={140} />
              ) : (
                <div className="flex items-center justify-center" style={{ height: 160, color: "var(--gt-text-tertiary)", fontSize: 13 }}>
                  No data yet — once attendance is logged it will appear here.
                </div>
              )}
            </button>

            {/* Tasks by priority — donut */}
            <button
              onClick={() => navigate("/tasks/list")}
              className="dash-tile text-left rounded-2xl p-6 lg:col-span-3"
              style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="dash-eyebrow mb-1.5">Tasks · By priority</div>
                  <h3
                    className="dash-serif"
                    style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
                  >
                    Where <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>focus</span> lives
                  </h3>
                </div>
                <ArrowRight className="dash-arrow w-4 h-4" style={{ color: "var(--gt-primary)" }} />
              </div>
              {(() => {
                const byP = (taskStats?.by_priority || {}) as Record<string, number>;
                const segs: Segment[] = [
                  { label: "Urgent", value: byP.URGENT || byP.urgent || 0, color: "var(--gt-prio-urgent-dot)" },
                  { label: "High", value: byP.HIGH || byP.high || 0, color: "var(--gt-prio-high-dot)" },
                  { label: "Medium", value: byP.MEDIUM || byP.medium || 0, color: "var(--gt-prio-med-dot)" },
                  { label: "Low", value: byP.LOW || byP.low || 0, color: "var(--gt-prio-low-dot)" },
                ];
                const total = segs.reduce((s, x) => s + x.value, 0);
                if (total === 0) {
                  return (
                    <div className="flex items-center justify-center" style={{ height: 180, color: "var(--gt-text-tertiary)", fontSize: 13 }}>
                      No active tasks — your team is clear.
                    </div>
                  );
                }
                return (
                  <div className="flex items-center gap-6">
                    <DonutChart
                      segments={segs}
                      size={150}
                      thickness={18}
                      centerValue={<Counter value={total} />}
                      centerLabel="open tasks"
                    />
                    <div className="flex-1 space-y-2.5">
                      {segs.map((s, i) => (
                        <div key={i} className="flex items-center justify-between" style={{ fontSize: 12.5 }}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                            <span style={{ fontWeight: 600, color: "var(--gt-text-secondary)" }}>{s.label}</span>
                          </span>
                          <span className="dash-mono" style={{ color: "var(--gt-text-tertiary)" }}>
                            <span style={{ color: "var(--gt-text-primary)", fontWeight: 600 }}>{s.value}</span> · {Math.round((s.value / total) * 100)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </button>

            {/* Tasks by status — donut */}
            <button
              onClick={() => navigate("/tasks/list")}
              className="dash-tile text-left rounded-2xl p-6 lg:col-span-2"
              style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
            >
              <div className="dash-eyebrow mb-1.5">Tasks · Health</div>
              <h3
                className="dash-serif mb-4"
                style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
              >
                Status <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>mix</span>
              </h3>
              {(() => {
                const open = taskStats?.open ?? stats?.openTasks ?? 0;
                const overdue = taskStats?.overdue ?? 0;
                const dueToday = taskStats?.due_today ?? 0;
                const done = stats?.completedTasks ?? taskStats?.done_this_month ?? 0;
                const segs: Segment[] = [
                  { label: "On track", value: Math.max(0, open - overdue - dueToday), color: "var(--gt-primary)" },
                  { label: "Due today", value: dueToday, color: "var(--gt-prio-med-dot)" },
                  { label: "Overdue", value: overdue, color: "var(--gt-prio-urgent-dot)" },
                  { label: "Done", value: done, color: "var(--gt-online)" },
                ];
                const total = segs.reduce((s, x) => s + x.value, 0);
                if (total === 0) {
                  return (
                    <div className="flex items-center justify-center" style={{ height: 200, color: "var(--gt-text-tertiary)", fontSize: 13 }}>
                      All clear.
                    </div>
                  );
                }
                return (
                  <div className="flex flex-col items-center gap-4">
                    <DonutChart
                      segments={segs}
                      size={140}
                      thickness={16}
                      centerValue={<Counter value={Math.round((done / Math.max(total, 1)) * 100)} suffix="%" />}
                      centerLabel="completed"
                    />
                    <div className="w-full space-y-1.5">
                      {segs.map((s, i) => (
                        <div key={i} className="flex items-center justify-between" style={{ fontSize: 11.5 }}>
                          <span className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                            <span style={{ color: "var(--gt-text-secondary)" }}>{s.label}</span>
                          </span>
                          <span className="dash-mono" style={{ color: "var(--gt-text-primary)", fontWeight: 600 }}>{s.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </button>

            {/* Leaves breakdown — stacked bar */}
            <button
              onClick={() => navigate("/leaves")}
              className="dash-tile text-left rounded-2xl p-6 lg:col-span-4"
              style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="dash-eyebrow mb-1.5">Leaves · Status</div>
                  <h3
                    className="dash-serif"
                    style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
                  >
                    Leave <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>pipeline</span>
                  </h3>
                </div>
                <div className="text-right">
                  <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 28, fontWeight: 500, letterSpacing: "-0.02em", color: "var(--gt-text-primary)", lineHeight: 1 }}>
                    <Counter value={leavesData.length} />
                  </div>
                  <div className="dash-eyebrow" style={{ fontSize: 9.5, marginTop: 4 }}>total requests</div>
                </div>
              </div>
              {(() => {
                const counts: Record<string, number> = {};
                leavesData.forEach((l: any) => {
                  const s = (l.status || "pending").toLowerCase();
                  counts[s] = (counts[s] || 0) + 1;
                });
                const segs: Segment[] = [
                  { label: "Approved", value: counts["approved"] || 0, color: "var(--gt-online)" },
                  { label: "Pending", value: counts["pending"] || 0, color: "var(--gt-away)" },
                  { label: "Rejected", value: counts["rejected"] || 0, color: "var(--gt-dnd)" },
                ];
                const total = segs.reduce((s, x) => s + x.value, 0);
                if (total === 0) {
                  return (
                    <div className="flex items-center justify-center" style={{ height: 100, color: "var(--gt-text-tertiary)", fontSize: 13 }}>
                      No leave applications yet.
                    </div>
                  );
                }
                return <StackedBar segments={segs} height={14} />;
              })()}

              {leavesData.length > 0 && (
                <div className="mt-5 pt-4 grid grid-cols-3 gap-3" style={{ borderTop: "1px dashed var(--gt-border-strong)" }}>
                  {(() => {
                    const byType: Record<string, number> = {};
                    leavesData.forEach((l: any) => {
                      const t = (l.leaveType || "personal").toLowerCase();
                      byType[t] = (byType[t] || 0) + 1;
                    });
                    return Object.entries(byType).slice(0, 3).map(([type, count]) => (
                      <div key={type}>
                        <div className="dash-eyebrow" style={{ fontSize: 9, marginBottom: 3 }}>{type}</div>
                        <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 500, color: "var(--gt-text-primary)", letterSpacing: "-0.02em", lineHeight: 1 }}>
                          {count}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </button>

            {/* Workforce — donut */}
            <button
              onClick={() => navigate("/employees")}
              className="dash-tile text-left rounded-2xl p-6 lg:col-span-2"
              style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
            >
              <div className="dash-eyebrow mb-1.5">Workforce · Composition</div>
              <h3
                className="dash-serif mb-4"
                style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
              >
                Active <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>workforce</span>
              </h3>
              {(() => {
                const total = stats?.total_employees || 0;
                const active = stats?.activeEmployees || 0;
                const inactive = Math.max(0, total - active);
                const segs: Segment[] = [
                  { label: "Active", value: active, color: "var(--gt-primary)" },
                  { label: "Inactive", value: inactive, color: "var(--gt-surface-3)" },
                ];
                if (total === 0) {
                  return (
                    <div className="flex items-center justify-center" style={{ height: 180, color: "var(--gt-text-tertiary)", fontSize: 13 }}>
                      Add your first employee.
                    </div>
                  );
                }
                return (
                  <div className="flex flex-col items-center gap-4">
                    <DonutChart
                      segments={segs}
                      size={140}
                      thickness={16}
                      centerValue={<Counter value={total > 0 ? Math.round((active / total) * 100) : 0} suffix="%" />}
                      centerLabel="active"
                    />
                    <div className="w-full flex items-center justify-around" style={{ fontSize: 12 }}>
                      <div className="text-center">
                        <div className="dash-mono" style={{ color: "var(--gt-text-primary)", fontWeight: 700 }}>{active}</div>
                        <div className="dash-eyebrow" style={{ fontSize: 9, marginTop: 2 }}>active</div>
                      </div>
                      <div className="w-px h-8" style={{ background: "var(--gt-border)" }} />
                      <div className="text-center">
                        <div className="dash-mono" style={{ color: "var(--gt-text-primary)", fontWeight: 700 }}>{inactive}</div>
                        <div className="dash-eyebrow" style={{ fontSize: 9, marginTop: 2 }}>inactive</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </button>

            {/* Projects — status mix + recent active list (fills the row beside Workforce) */}
            <button
              onClick={() => navigate("/projects")}
              className="dash-tile text-left rounded-2xl p-6 lg:col-span-4"
              style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
            >
              {(() => {
                const items = Array.isArray(projectsList) ? projectsList : [];

                // Bucket by status. Be liberal about field names.
                const norm = (v: any) => (v || "").toString().toLowerCase().replace(/[\s_-]+/g, "");
                const counts = { active: 0, completed: 0, onhold: 0, planned: 0, other: 0 };
                items.forEach((p: any) => {
                  const s = norm(p.status || p.state || p.project_status);
                  if (s === "active" || s === "inprogress" || s === "ongoing") counts.active++;
                  else if (s === "completed" || s === "done" || s === "finished") counts.completed++;
                  else if (s === "onhold" || s === "paused" || s === "blocked") counts.onhold++;
                  else if (s === "planned" || s === "todo" || s === "notstarted" || s === "draft") counts.planned++;
                  else counts.other++;
                });

                const total = items.length;
                const segs: Segment[] = [
                  { label: "Active",    value: counts.active,    color: "var(--gt-primary)" },
                  { label: "Planned",   value: counts.planned,   color: "var(--gt-prio-med-dot)" },
                  { label: "On hold",   value: counts.onhold,    color: "var(--gt-away)" },
                  { label: "Completed", value: counts.completed, color: "var(--gt-online)" },
                ];
                if (counts.other > 0) {
                  segs.push({ label: "Other", value: counts.other, color: "var(--gt-surface-3)" });
                }

                // Pick a few recent active projects.
                const recent = items
                  .filter((p: any) => {
                    const s = norm(p.status || p.state || p.project_status);
                    return !["completed", "done", "finished"].includes(s);
                  })
                  .slice(0, 4);

                const fmt = (d: any) => {
                  if (!d) return null;
                  const dt = new Date(d);
                  if (isNaN(dt.getTime())) return null;
                  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
                };

                return (
                  <>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="dash-eyebrow mb-1.5">Projects · Health</div>
                        <h3
                          className="dash-serif"
                          style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 20, fontWeight: 500, letterSpacing: "-0.015em", color: "var(--gt-text-primary)" }}
                        >
                          Active <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>portfolio</span>
                        </h3>
                      </div>
                      <div className="text-right">
                        <div className="dash-serif" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 28, fontWeight: 500, letterSpacing: "-0.02em", color: "var(--gt-text-primary)", lineHeight: 1 }}>
                          <Counter value={total} />
                        </div>
                        <div className="dash-eyebrow" style={{ fontSize: 9.5, marginTop: 4 }}>total projects</div>
                      </div>
                    </div>

                    {total === 0 ? (
                      <div className="flex items-center justify-center" style={{ height: 160, color: "var(--gt-text-tertiary)", fontSize: 13 }}>
                        No projects yet — create one to see status here.
                      </div>
                    ) : (
                      <>
                        <StackedBar segments={segs} height={14} />

                        {recent.length > 0 && (
                          <ul className="mt-5 pt-4 space-y-2.5" style={{ borderTop: "1px dashed var(--gt-border-strong)" }}>
                            {recent.map((p: any, i: number) => {
                              const status = norm(p.status || p.state || p.project_status);
                              const isOnHold = ["onhold", "paused", "blocked"].includes(status);
                              const dotColor = isOnHold ? "var(--gt-away)" : "var(--gt-primary)";
                              const due = fmt(p.endDate || p.end_date || p.dueDate || p.due_date || p.deadline);
                              return (
                                <li
                                  key={p.id || p._id || i}
                                  className="flex items-center justify-between gap-3 py-1"
                                  style={{ borderBottom: i < recent.length - 1 ? "1px dashed var(--gt-border)" : "none" }}
                                >
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: dotColor }} />
                                    <div className="min-w-0">
                                      <div className="truncate" style={{ fontSize: 13, fontWeight: 600, color: "var(--gt-text-primary)" }}>
                                        {p.name || p.project_name || p.title || "Untitled project"}
                                      </div>
                                      <div className="dash-eyebrow" style={{ fontSize: 9.5, marginTop: 1 }}>
                                        {(p.client_name || p.client || p.department || "—").toString()} · {(p.status || p.state || "active").toString()}
                                      </div>
                                    </div>
                                  </div>
                                  {due && (
                                    <div className="text-right flex-shrink-0">
                                      <div className="dash-mono" style={{ fontSize: 12, color: "var(--gt-text-primary)", fontWeight: 600 }}>
                                        {due}
                                      </div>
                                      <div className="dash-eyebrow" style={{ fontSize: 9, marginTop: 1 }}>due</div>
                                    </div>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </>
                    )}
                  </>
                );
              })()}
            </button>
          </div>
        </div>

        {/* MODULES GRID — every Teamlio feature in one place */}
        <div>
          <div className="dash-reveal flex items-end justify-between mb-5 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="gt-leaf" />
                <span className="dash-eyebrow">iii. Modules</span>
              </div>
              <h2
                className="dash-serif"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: "clamp(24px, 2.6vw, 34px)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  fontWeight: 500,
                  color: "var(--gt-text-primary)",
                }}
              >
                Everything <span style={{ fontStyle: "italic" }}>at a glance</span>
              </h2>
            </div>
            <div className="dash-mono" style={{ fontSize: 11, color: "var(--gt-text-tertiary)", letterSpacing: "0.18em", textTransform: "uppercase" }}>
              {user?.role === "employee" ? "06 modules" : "09 modules"}
            </div>
          </div>

          <div className="dash-reveal dash-stagger grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {(() => {
              // Compute summaries from real data
              const totalEmp = stats?.total_employees || 0;
              const activeEmp = stats?.activeEmployees || 0;
              const inactiveEmp = Math.max(0, totalEmp - activeEmp);
              const present = stats?.todays_present || 0;
              const away = Math.max(0, totalEmp - present);
              const presentPct = totalEmp > 0 ? Math.round((present / totalEmp) * 100) : 0;

              const leaveCounts = leavesData.reduce((acc: any, l: any) => {
                const s = (l.status || "pending").toLowerCase();
                acc[s] = (acc[s] || 0) + 1;
                return acc;
              }, {});
              const lvPending = leaveCounts.pending || stats?.pending_leaves || 0;
              const lvApproved = leaveCounts.approved || 0;

              const dueToday = taskStats?.due_today || 0;
              const overdue = taskStats?.overdue || 0;
              const openTasks = stats?.openTasks || taskStats?.open || 0;
              const doneTasks = stats?.completedTasks || taskStats?.done_this_month || 0;

              const projActive = projectsList.filter((p) => (p.status || "ACTIVE") === "ACTIVE").length;
              const projOnHold = projectsList.filter((p) => p.status === "ON_HOLD").length;
              const projDone = projectsList.filter((p) => p.status === "COMPLETED").length;
              const projTotal = projectsList.length;

              const slPending = salaryStatus?.pending || 0;
              const slApproved = salaryStatus?.approved || 0;
              const slTotal = salaryStatus?.total || 0;

              const all = [
                {
                  n: "01", title: "Employees", desc: "Profiles, roles, bulk import",
                  Icon: Users, href: "/employees",
                  primary: { v: totalEmp, l: "On board" },
                  secondary: { v: activeEmp, l: "Active" },
                  tertiary: inactiveEmp > 0 ? `${inactiveEmp} inactive` : undefined,
                },
                {
                  n: "02", title: "Attendance", desc: "GPS clock-ins, daily status",
                  Icon: Clock, href: "/attendance",
                  primary: { v: present, l: "Present today" },
                  secondary: { v: away, l: "Away" },
                  tertiary: `${presentPct}% rate`,
                },
                {
                  n: "03", title: "Leaves", desc: "Requests, balances, approvals",
                  Icon: Calendar, href: "/leaves",
                  primary: { v: lvPending, l: "Pending" },
                  secondary: { v: lvApproved, l: "Approved" },
                  tertiary: leavesData.length > 0 ? `${leavesData.length} total` : undefined,
                },
                {
                  n: "04", title: "Daily Planner", desc: "Today / Upcoming / Missed",
                  Icon: ClipboardList, href: "/todo",
                  primary: { v: dueToday, l: "Due today" },
                  secondary: { v: overdue, l: "Overdue" },
                  tertiary: taskStats?.unassigned ? `${taskStats.unassigned} unassigned` : undefined,
                },
                {
                  n: "05", title: "Tasks", desc: "Assign, track, complete",
                  Icon: CheckSquare, href: "/tasks/list",
                  primary: { v: openTasks, l: "Active" },
                  secondary: { v: doneTasks, l: "Done" },
                  tertiary: taskStats?.blocked ? `${taskStats.blocked} blocked` : undefined,
                },
                {
                  n: "06", title: "Projects", desc: "Kanban, clients, milestones",
                  Icon: FolderOpen, href: "/projects", adminOnly: true,
                  primary: { v: projActive, l: "Active" },
                  secondary: { v: projDone, l: "Done" },
                  tertiary: projOnHold > 0 ? `${projOnHold} on hold` : projTotal > 0 ? `${projTotal} total` : undefined,
                },
                {
                  n: "07", title: "Work Monitor", desc: "Activity, screenshots, reports",
                  Icon: Activity, href: "/activity-reports", adminOnly: true,
                  primary: { v: activeEmp, l: "Tracked" },
                  secondary: { v: present, l: "Online today" },
                  tertiary: "Live activity",
                },
                {
                  n: "08", title: "Payroll", desc: "Salaries, payslips, approvals",
                  Icon: Wallet, href: "/payroll-record", adminOnly: true,
                  primary: { v: slApproved, l: "Approved" },
                  secondary: { v: slPending, l: "Pending" },
                  tertiary: slTotal > 0 ? `${slTotal} this month` : new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" }),
                },
                {
                  n: "09", title: "Downloads", desc: "Mobile & desktop apps",
                  Icon: Download, href: "/downloads",
                  primary: { v: 3, l: "Platforms" },
                  secondary: { v: 2, l: "Apps" },
                  tertiary: "iOS · Android · Web",
                },
              ];
              const visible = user?.role === "employee" ? all.filter((m) => !m.adminOnly) : all;
              return visible.map((mod) => (
                <button
                  key={mod.n}
                  onClick={() => navigate(mod.href)}
                  className="dash-tile text-left rounded-2xl p-5 relative group"
                  style={{
                    background: "var(--gt-surface-0)",
                    border: "1px solid var(--gt-border)",
                  }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="dash-mono" style={{ fontSize: 10, letterSpacing: "0.18em", color: "var(--gt-text-tertiary)" }}>
                      № {mod.n}
                    </span>
                    <span
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300"
                      style={{ background: "var(--gt-primary-container)", color: "var(--gt-primary-deep)" }}
                    >
                      <mod.Icon className="w-4 h-4" />
                    </span>
                  </div>
                  <div
                    className="dash-serif mb-1"
                    style={{
                      fontFamily: "'Fraunces', Georgia, serif",
                      fontSize: 18,
                      letterSpacing: "-0.015em",
                      lineHeight: 1.15,
                      fontWeight: 500,
                      color: "var(--gt-text-primary)",
                    }}
                  >
                    {mod.title}
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--gt-text-tertiary)", lineHeight: 1.4, marginBottom: 12 }}>{mod.desc}</div>

                  {/* Two-stat summary */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <div
                        className="dash-serif"
                        style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 500, letterSpacing: "-0.025em", color: "var(--gt-text-primary)", lineHeight: 1 }}
                      >
                        <Counter value={Number(mod.primary.v) || 0} />
                      </div>
                      <div className="dash-eyebrow" style={{ fontSize: 8.5, marginTop: 3 }}>{mod.primary.l}</div>
                    </div>
                    <div>
                      <div
                        className="dash-serif"
                        style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 500, letterSpacing: "-0.025em", color: "var(--gt-text-secondary)", lineHeight: 1 }}
                      >
                        <Counter value={Number(mod.secondary.v) || 0} />
                      </div>
                      <div className="dash-eyebrow" style={{ fontSize: 8.5, marginTop: 3 }}>{mod.secondary.l}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3" style={{ borderTop: "1px dashed var(--gt-border-strong)" }}>
                    <span className="dash-mono" style={{ fontSize: 9.5, color: "var(--gt-primary)", letterSpacing: "0.16em", textTransform: "uppercase" }}>
                      {mod.tertiary || "Open"}
                    </span>
                    <ArrowRight className="dash-arrow w-3.5 h-3.5" style={{ color: "var(--gt-primary)" }} />
                  </div>
                </button>
              ));
            })()}
          </div>
        </div>

        {/* HEALTH ROW — Attendance donut + Org Pulse */}
        {(() => {
          const totalEmp = stats?.total_employees || 0;
          const presentToday = stats?.todays_present || 0;
          const attendancePct = totalEmp > 0 ? Math.round((presentToday / totalEmp) * 100) : 0;
          const openTasks = stats?.openTasks || 0;
          const completedTasks = stats?.completedTasks || 0;
          const totalTasks = openTasks + completedTasks;
          const taskCompletionPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
          const donutR = 56;
          const donutC = 2 * Math.PI * donutR;
          const breakdown = [
            { label: "Present today", count: presentToday, total: totalEmp || 1, color: "var(--gt-online)" },
            { label: "Pending leaves", count: stats?.pending_leaves || 0, total: Math.max(totalEmp || 1, 1), color: "var(--gt-away)" },
            { label: "Active tasks", count: openTasks, total: totalTasks || 1, color: "var(--gt-primary)" },
          ];
          return (
            <div className="dash-reveal grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Attendance donut */}
              <button
                onClick={() => navigate("/attendance")}
                className="dash-tile text-left rounded-2xl p-6 lg:col-span-1 relative"
                style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="gt-leaf" />
                    <span className="dash-eyebrow">i. Today</span>
                  </div>
                  <ArrowRight className="dash-arrow w-3.5 h-3.5" style={{ color: "var(--gt-primary)" }} />
                </div>
                <h3
                  className="dash-serif mb-3"
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: 20,
                    letterSpacing: "-0.015em",
                    fontWeight: 500,
                    color: "var(--gt-text-primary)",
                  }}
                >
                  Attendance <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>Health</span>
                </h3>
                <div className="flex items-center justify-center" style={{ padding: "8px 0" }}>
                  <div style={{ position: "relative", width: 160, height: 160 }}>
                    <svg width={160} height={160} viewBox="0 0 160 160" style={{ transform: "rotate(-90deg)" }}>
                      <circle cx={80} cy={80} r={donutR} fill="none" stroke="var(--gt-surface-2)" strokeWidth={12} />
                      <circle
                        cx={80}
                        cy={80}
                        r={donutR}
                        fill="none"
                        stroke="var(--gt-primary)"
                        strokeWidth={12}
                        strokeLinecap="round"
                        strokeDasharray={donutC}
                        strokeDashoffset={donutC * (1 - attendancePct / 100)}
                        style={{ transition: "stroke-dashoffset 1200ms cubic-bezier(0.22, 1, 0.36, 1)" }}
                      />
                    </svg>
                    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                      <span
                        className="dash-serif"
                        style={{
                          fontFamily: "'Fraunces', Georgia, serif",
                          fontSize: 40,
                          fontWeight: 500,
                          letterSpacing: "-0.025em",
                          color: "var(--gt-text-primary)",
                          lineHeight: 1,
                        }}
                      >
                        <Counter value={attendancePct} />
                        <span style={{ fontSize: 18, color: "var(--gt-text-tertiary)" }}>%</span>
                      </span>
                      <span className="dash-eyebrow" style={{ marginTop: 4 }}>present</span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between dash-mono" style={{ marginTop: 12, fontSize: 11.5 }}>
                  <span style={{ color: "var(--gt-online)" }}>● {presentToday} present</span>
                  <span style={{ color: "var(--gt-text-tertiary)" }}>● {Math.max(totalEmp - presentToday, 0)} away</span>
                </div>
              </button>

              {/* Org Pulse */}
              <div
                className="rounded-2xl p-6 lg:col-span-2 relative"
                style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" style={{ color: "var(--gt-primary)" }} />
                    <h3
                      className="dash-serif"
                      style={{
                        fontFamily: "'Fraunces', Georgia, serif",
                        fontSize: 20,
                        letterSpacing: "-0.015em",
                        fontWeight: 500,
                        color: "var(--gt-text-primary)",
                      }}
                    >
                      Organisation <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>Pulse</span>
                    </h3>
                  </div>
                  <span className="inline-flex items-center gap-1.5 dash-mono" style={{ fontSize: 10, color: "var(--gt-online)", letterSpacing: "0.2em", textTransform: "uppercase" }}>
                    <span className="dash-pulse w-1.5 h-1.5 rounded-full" style={{ background: "var(--gt-online)" }} />
                    Live
                  </span>
                </div>

                <div className="space-y-4">
                  {breakdown.map((row) => {
                    const pct = Math.min(100, Math.round((row.count / row.total) * 100));
                    return (
                      <div key={row.label}>
                        <div className="flex justify-between mb-1.5" style={{ fontSize: 12.5 }}>
                          <span style={{ fontWeight: 600, color: "var(--gt-text-secondary)" }}>{row.label}</span>
                          <span className="dash-mono" style={{ color: "var(--gt-text-tertiary)" }}>
                            <span style={{ color: "var(--gt-text-primary)", fontWeight: 600 }}>{row.count}</span> · {pct}%
                          </span>
                        </div>
                        <div style={{ height: 8, background: "var(--gt-surface-2)", borderRadius: 999, overflow: "hidden" }}>
                          <div
                            style={{
                              height: "100%",
                              borderRadius: 999,
                              background: row.color,
                              width: `${pct}%`,
                              transition: "width 1200ms cubic-bezier(0.22, 1, 0.36, 1)",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div
                  className="grid grid-cols-2 gap-3 mt-6 pt-5"
                  style={{ borderTop: "1px dashed var(--gt-border-strong)" }}
                >
                  <div>
                    <span className="dash-eyebrow block" style={{ marginBottom: 4 }}>Task completion</span>
                    <span
                      className="dash-serif"
                      style={{
                        fontFamily: "'Fraunces', Georgia, serif",
                        fontSize: 26,
                        fontWeight: 500,
                        letterSpacing: "-0.025em",
                        color: "var(--gt-text-primary)",
                      }}
                    >
                      <Counter value={taskCompletionPct} suffix="%" />
                    </span>
                    <span className="dash-serif" style={{ fontStyle: "italic", fontSize: 12, marginLeft: 6, color: "var(--gt-text-tertiary)" }}>finished</span>
                  </div>
                  <div>
                    <span className="dash-eyebrow block" style={{ marginBottom: 4 }}>Active workforce</span>
                    <span
                      className="dash-serif"
                      style={{
                        fontFamily: "'Fraunces', Georgia, serif",
                        fontSize: 26,
                        fontWeight: 500,
                        letterSpacing: "-0.025em",
                        color: "var(--gt-text-primary)",
                      }}
                    >
                      <Counter value={stats?.activeEmployees || 0} />
                    </span>
                    <span className="dash-serif" style={{ fontStyle: "italic", fontSize: 12, marginLeft: 6, color: "var(--gt-text-tertiary)" }}>of {totalEmp}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* QUICK ACTIONS + ACTIVITIES */}
        <div className="dash-reveal grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Quick actions */}
          <div
            className="rounded-2xl p-6"
            style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <PlusCircle className="w-4 h-4" style={{ color: "var(--gt-primary)" }} />
              <h3
                className="dash-serif"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: 18,
                  fontWeight: 500,
                  letterSpacing: "-0.015em",
                  color: "var(--gt-text-primary)",
                }}
              >
                Quick <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>actions</span>
              </h3>
            </div>
            <p style={{ fontSize: 12, color: "var(--gt-text-tertiary)", marginBottom: 18 }}>
              Common tasks and shortcuts
            </p>

            <div className="space-y-2.5">
              {(user?.role === "employee"
                ? [
                    { label: "Clock in / out", Icon: Clock, href: "/attendance" },
                    { label: "Apply for leave", Icon: Calendar, href: "/leaves" },
                    { label: "View my tasks", Icon: CheckSquare, href: "/tasks/my-tasks" },
                  ]
                : [
                    { label: "Add an employee", Icon: UserPlus, href: "/employees" },
                    { label: "Approve leaves", Icon: Calendar, href: "/leaves" },
                    { label: "Open reports", Icon: FileText, href: "/tasks/reports" },
                    { label: "Run payroll", Icon: Wallet, href: "/payroll-record" },
                    { label: "Download apps", Icon: Download, href: "/downloads" },
                  ]
              ).map((a, i) => (
                <button
                  key={i}
                  onClick={() => navigate(a.href)}
                  className="dash-tile w-full text-left flex items-center gap-3 rounded-xl px-4 py-3"
                  style={{
                    background: "var(--gt-surface-1)",
                    border: "1px solid var(--gt-border)",
                  }}
                >
                  <span
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: "var(--gt-primary-container)", color: "var(--gt-primary-deep)" }}
                  >
                    <a.Icon className="w-4 h-4" />
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--gt-text-primary)" }}>{a.label}</span>
                  <ArrowRight className="dash-arrow w-3.5 h-3.5 ml-auto" style={{ color: "var(--gt-primary)" }} />
                </button>
              ))}
            </div>
          </div>

          {/* Today's activities */}
          <div
            className="lg:col-span-2 rounded-2xl p-6"
            style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="dash-pulse w-2 h-2 rounded-full" style={{ background: "var(--gt-online)" }} />
                <Bell className="w-4 h-4" style={{ color: "var(--gt-primary)" }} />
                <h3
                  className="dash-serif"
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: 18,
                    fontWeight: 500,
                    letterSpacing: "-0.015em",
                    color: "var(--gt-text-primary)",
                  }}
                >
                  Today's <span style={{ fontStyle: "italic", color: "var(--gt-primary)" }}>activities</span>
                </h3>
              </div>
              {activities.length > 4 && (
                <div className="flex gap-1.5">
                  {Array.from({ length: Math.min(5, Math.ceil(activities.length / 4)) }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        const startIdx = idx * 4;
                        setDisplayActivities(activities.slice(startIdx, startIdx + 4));
                        setCurrentPageIndex(idx);
                      }}
                      className="rounded-full transition-all duration-300"
                      style={{
                        width: currentPageIndex === idx ? 22 : 6,
                        height: 6,
                        background: currentPageIndex === idx ? "var(--gt-primary)" : "var(--gt-border-strong)",
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
            <p style={{ fontSize: 12, color: "var(--gt-text-tertiary)", marginBottom: 16 }}>
              Attendance · Leaves · Salary · Tasks
            </p>

            {currentDisplay.length === 0 ? (
              <div className="text-center py-10" style={{ color: "var(--gt-text-tertiary)" }}>
                <Bell className="w-9 h-9 mx-auto mb-2" style={{ opacity: 0.4 }} />
                <p className="text-sm">No recent activity</p>
                <p className="text-xs mt-1" style={{ opacity: 0.7 }}>Updates will appear here as your team works</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {currentDisplay.map((activity, idx) => (
                  <div
                    key={activity.id || `${activity.displayType}-${idx}-${activity.time}`}
                    className="dash-tile flex items-center justify-between p-3.5 rounded-xl group"
                    style={{
                      background: "var(--gt-surface-1)",
                      border: "1px solid var(--gt-border)",
                      animationDelay: `${idx * 0.06}s`,
                    }}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-full flex-shrink-0 ${getActivityColorClass(activity.color)}`}>
                        {getActivityIconComponent(activity.icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p style={{ fontSize: 13.5, fontWeight: 600, color: "var(--gt-text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {activity.displayMessage}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="dash-mono" style={{ fontSize: 10.5, color: "var(--gt-text-tertiary)" }}>
                            {getTimeAgo(activity.time || activity.timestamp)}
                          </span>
                          {activity.badge && (
                            <Badge variant="outline" className={`text-xs ${getBadgeColorClass(activity.badgeColor)}`}>
                              {activity.badge}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 dash-arrow" style={{ color: "var(--gt-text-tertiary)" }} />
                  </div>
                ))}
              </div>
            )}

            {activities.length > 4 && (
              <div
                className="mt-5 pt-3 text-center dash-mono"
                style={{ borderTop: "1px dashed var(--gt-border-strong)", fontSize: 10.5, color: "var(--gt-text-tertiary)", letterSpacing: "0.16em", textTransform: "uppercase" }}
              >
                Page {currentPageIndex + 1} of {Math.ceil(activities.length / 4)} · auto-rotating
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// IntersectionObserver runner for .dash-reveal elements
function DashRevealRunner() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-revealed");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".dash-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return null;

}